import { ENVIRONMENT } from '@/constants/env'
import { readFromLocalStorage, setToLocalStorage } from '@/services/localStorage'
import { getCountryFromCurrency } from '@/utils/country.utils'

const DEVICE_ID_KEY = 'clarityDeviceId'

function generateUUID(): string {
  return crypto.randomUUID()
}

export function getDeviceId(): string {
  let deviceId = readFromLocalStorage(DEVICE_ID_KEY)

  if (!deviceId) {
    deviceId = generateUUID()
    setToLocalStorage({ [DEVICE_ID_KEY]: deviceId })
  }

  return deviceId
}

/**
 * Detect device type (mobile/tablet/desktop)
 */
function getDeviceType(): string {
  const userAgent = navigator.userAgent

  // Detect specific mobile devices
  if (/iPhone/.test(userAgent)) return 'iPhone'
  if (/iPad/.test(userAgent)) return 'iPad'
  if (/iPod/.test(userAgent)) return 'iPod'
  
  // Detect Android devices
  if (/Android/.test(userAgent)) {
    // Try to get specific Android device info
    const match = userAgent.match(/Android.*;\s*([^)]+)\s*Build/)
    if (match && match[1]) {
      return `Android (${match[1].trim()})`
    }
    return 'Android'
  }

  // Detect Windows Phone
  if (/Windows Phone/.test(userAgent)) return 'Windows Phone'

  // Detect tablets
  if (/Tablet|PlayBook/.test(userAgent)) return 'Tablet'

  // Detect mobile (generic)
  if (/Mobile|webOS|BlackBerry|IEMobile|Opera Mini/.test(userAgent)) {
    return 'Mobile'
  }

  // Desktop - detect OS
  if (/Macintosh|Mac OS X/.test(userAgent)) return 'Mac'
  if (/Windows NT/.test(userAgent)) return 'Windows'
  if (/Linux/.test(userAgent)) return 'Linux'
  if (/CrOS/.test(userAgent)) return 'Chrome OS'

  return 'Desktop'
}

export function getDeviceModel(): string {
  const userAgent = navigator.userAgent
  let browser = 'Unknown'
  let version = ''

  // Detect browser
  if (userAgent.indexOf('Firefox') > -1) {
    browser = 'Firefox'
    version = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || ''
  } else if (userAgent.indexOf('Edg') > -1) {
    browser = 'Edge'
    version = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || ''
  } else if (userAgent.indexOf('Chrome') > -1) {
    browser = 'Chrome'
    version = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || ''
  } else if (userAgent.indexOf('Safari') > -1) {
    browser = 'Safari'
    version = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || ''
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browser = 'Opera'
    version = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || ''
  }

  const browserInfo = version ? `${browser} ${version}` : browser
  const deviceType = getDeviceType()

  // Combine browser and device info
  return `${browserInfo} (${deviceType})`
}

export interface PreLoginClarityProperties {
  deviceId: string
  deviceModel: string
  environment: string
}

export interface PostLoginClarityProperties extends PreLoginClarityProperties {
  storeId: string
  storeType: string
  platform: string
  country: string | null
  city?: string
}

export function setClarityCustomProperties(
  properties: Partial<PostLoginClarityProperties>
): void {
  if (typeof window === 'undefined' || !window.clarity) {
    return
  }

  const clarity = window.clarity
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined && value !== null && clarity) {
      clarity('set', key, String(value))
    }
  })
}

export function initializeClarityPreLogin(): void {
  if (typeof window === 'undefined' || ENVIRONMENT === 'development') {
    return
  }

  const properties: PreLoginClarityProperties = {
    deviceId: getDeviceId(),
    deviceModel: getDeviceModel(),
    environment: ENVIRONMENT
  }

  setClarityCustomProperties(properties)
}

export function setClarityPostLogin(loginData: {
  storeId: string
  storeType: string
  currencyCode: string
  address?: string
}): void {
  if (typeof window === 'undefined' || ENVIRONMENT === 'development') {
    return
  }

  const country = getCountryFromCurrency(loginData.currencyCode)
  let city: string | undefined
  if (loginData.address) {
    const parts = loginData.address.split(',').map(p => p.trim())
    if (parts.length >= 2) {
      city = parts[parts.length >= 3 ? parts.length - 2 : 0]
    }
  }

  const properties: PostLoginClarityProperties = {
    deviceId: getDeviceId(),
    deviceModel: getDeviceModel(),
    environment: ENVIRONMENT,
    storeId: loginData.storeId,
    storeType: loginData.storeType,
    platform: 'web',
    country: country || 'Unknown',
    ...(city && { city })
  }

  setClarityCustomProperties(properties)
}

/**
 * Clear store-specific properties on logout
 * Keeps only pre-login properties
 */
export function clearClarityStoreProperties(): void {
  if (typeof window === 'undefined' || ENVIRONMENT === 'development') {
    return
  }

  const storeToClear = {
    storeId: '',
    storeType: '',
    platform: '',
    country: '',
    city: ''
  }

  setClarityCustomProperties(storeToClear)
  initializeClarityPreLogin()
}
